import { useEffect, useRef } from 'react';

/**
 * Drag handle between panels.
 * - `horizontal` bar → resizes height (axis Y)
 * - `vertical` bar → resizes width (axis X)
 */
export function ResizeHandle({
  orientation,
  value,
  onChange,
  min,
  max,
  /** When true, dragging positive (right/down) decreases the value (e.g. left edge of right panel). */
  invert = false,
  className = '',
}: {
  orientation: 'horizontal' | 'vertical';
  value: number;
  onChange: (next: number) => void;
  min: number;
  max: number;
  invert?: boolean;
  className?: string;
}) {
  const drag = useRef<{ start: number; value: number } | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!drag.current) return;
      const pos = orientation === 'vertical' ? e.clientX : e.clientY;
      const delta = pos - drag.current.start;
      const signed = invert ? -delta : delta;
      const next = Math.min(max, Math.max(min, drag.current.value + signed));
      onChangeRef.current(next);
    };
    const onUp = () => {
      drag.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [orientation, invert, min, max]);

  const isVert = orientation === 'vertical';

  return (
    <div
      role="separator"
      aria-orientation={isVert ? 'vertical' : 'horizontal'}
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={max}
      title="Drag to resize"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        drag.current = {
          start: isVert ? e.clientX : e.clientY,
          value: valueRef.current,
        };
        document.body.style.cursor = isVert ? 'col-resize' : 'row-resize';
        document.body.style.userSelect = 'none';
      }}
      onDoubleClick={() => {
        // Reset toward midpoint of min/max on double-click
        onChange(Math.round((min + max) / 2));
      }}
      className={`group relative z-40 shrink-0 bg-hairline transition-colors hover:bg-accent ${
        isVert ? 'w-px cursor-col-resize' : 'h-px cursor-row-resize'
      } ${className}`}
    >
      {/* Hit area */}
      <div
        className={`absolute ${
          isVert
            ? 'inset-y-0 -left-1 w-2.5'
            : 'inset-x-0 -top-1 h-2.5'
        }`}
      />
    </div>
  );
}
